import { useState, useEffect } from 'react';

interface GMBConnectionGateProps {
  onConnect: (setup: {
    gmb_profile_url: string;
    business_name: string;
    business_description: string;
  }) => void;
  onSkip: () => void;
  isLoading: boolean;
  error: string | null;
  darkMode: boolean;
  profileSetup: {
    gmb_profile_url: string;
    business_name: string;
    business_description: string;
  };
}

export default function GMBConnectionGate({
  onConnect,
  onSkip,
  isLoading,
  error,
  darkMode,
  profileSetup,
}: GMBConnectionGateProps) {
  const [form, setForm] = useState(profileSetup);
  const [validationError, setValidationError] = useState('');

  useEffect(() => setForm(profileSetup), [profileSetup]);

  const handleConnect = () => {
    const url = form.gmb_profile_url.trim();
    if (!/^https?:\/\//i.test(url)) {
      setValidationError('Enter a valid GMB Profile URL beginning with http:// or https://.');
      return;
    }
    if (!form.business_name.trim()) {
      setValidationError('Business Name is required.');
      return;
    }
    setValidationError('');
    onConnect({
      gmb_profile_url: url,
      business_name: form.business_name.trim(),
      business_description: form.business_description.trim(),
    });
  };

  return (
    <div className="flex items-center justify-center min-h-[500px]">
      <div
        className={`w-full max-w-md rounded-2xl border shadow-sm p-8 text-center ${
          darkMode
            ? 'bg-gray-800 border-gray-700'
            : 'bg-white border-gray-200'
        }`}
      >
        <div
          className={`w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6 ${
            darkMode ? 'bg-teal-900/30' : 'bg-teal-50'
          }`}
        >
          <i className="ri-links-line text-3xl text-teal-600"></i>
        </div>

        <h2
          className={`text-xl font-semibold mb-2 ${
            darkMode ? 'text-white' : 'text-gray-900'
          }`}
        >
          Connect Google Business Profile
        </h2>

        <p
          className={`text-sm mb-6 ${
            darkMode ? 'text-gray-400' : 'text-gray-500'
          }`}
        >
          Connect your GMB account to unlock powerful features for managing
          your Google Business Profile.
        </p>

        <div className="space-y-4 mb-8 text-left">
          <div>
            <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              GMB Profile URL <span className="text-red-500">*</span>
            </label>
            <input
              value={form.gmb_profile_url}
              onChange={(event) => setForm({ ...form, gmb_profile_url: event.target.value })}
              placeholder="https://www.google.com/maps?cid=..."
              className={`w-full rounded-lg border px-3 py-2 text-sm ${darkMode ? 'bg-gray-900 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            />
          </div>
          <div>
            <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Business Name <span className="text-red-500">*</span>
            </label>
            <input
              value={form.business_name}
              onChange={(event) => setForm({ ...form, business_name: event.target.value })}
              className={`w-full rounded-lg border px-3 py-2 text-sm ${darkMode ? 'bg-gray-900 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            />
          </div>
          <div>
            <label className={`block text-xs font-semibold mb-1 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Business Description <span className="text-xs font-normal opacity-70">(optional)</span>
            </label>
            <textarea
              value={form.business_description}
              onChange={(event) => setForm({ ...form, business_description: event.target.value })}
              rows={4}
              className={`w-full rounded-lg border px-3 py-2 text-sm resize-y ${darkMode ? 'bg-gray-900 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            />
          </div>
          {[
            { icon: 'ri-lightbulb-line', text: 'Strategy insights & health score' },
            { icon: 'ri-image-line', text: 'Photo & post management' },
            { icon: 'ri-star-line', text: 'Review monitoring & AI responses' },
            { icon: 'ri-bar-chart-line', text: 'Activity governance & alerts' },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <div
                className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                  darkMode ? 'bg-gray-700' : 'bg-gray-100'
                }`}
              >
                <i
                  className={`${item.icon} text-teal-600 text-sm`}
                ></i>
              </div>
              <span
                className={`text-sm ${
                  darkMode ? 'text-gray-300' : 'text-gray-600'
                }`}
              >
                {item.text}
              </span>
            </div>
          ))}
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        {validationError && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {validationError}
          </div>
        )}

        <button
          onClick={handleConnect}
          disabled={isLoading}
          className="w-full py-3 px-4 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold text-sm transition-colors disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isLoading ? (
            <>
              <i className="ri-loader-4-line animate-spin"></i>
              Connecting...
            </>
          ) : (
            <>
              <i className="ri-google-fill"></i>
              Connect GMB
            </>
          )}
        </button>

        <button
          onClick={onSkip}
          disabled={isLoading}
          className={`mt-3 text-sm transition-colors disabled:opacity-60 ${
            darkMode
              ? 'text-gray-400 hover:text-gray-300'
              : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          Set up later
        </button>
      </div>
    </div>
  );
}
